import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import TalentForm from './components/TalentForm';
import talentService from '../../services/talentService';
import { RoleService, assignTalentToRole } from '../../services/roleService';
import { fetchWarmUps, updateWarmUp } from '../../services/warmUpService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const AddEditTalentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userProfile, isSuperUser } = useAuth();
  const [talent, setTalent] = useState(null);
  const [talentVideos, setTalentVideos] = useState([]);
  const [talentRoles, setTalentRoles] = useState([]);
  const [talentWarmUps, setTalentWarmUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [videosLoading, setVideosLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [warmUpsLoading, setWarmUpsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [removingVideoId, setRemovingVideoId] = useState(null);
  const [removingWarmUpId, setRemovingWarmUpId] = useState(null);

  // Role assignment state
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [roleSearchResults, setRoleSearchResults] = useState([]);
  const [roleSearchLoading, setRoleSearchLoading] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [assigningRole, setAssigningRole] = useState(false);
  const [unassigningRoleId, setUnassigningRoleId] = useState(null);
  const roleSearchRef = useRef(null);

  const isEditing = Boolean(id);

  // Redirect if not superuser
  useEffect(() => {
    if (user && !isSuperUser()) {
      navigate('/song-database-list');
    }
  }, [user, isSuperUser, navigate]);

  // Load talent data for editing
  useEffect(() => {
    if (isEditing && id && user && isSuperUser()) {
      loadTalentData();
      loadTalentVideos();
      loadTalentRoles();
      loadTalentWarmUps();
    }
  }, [id, isEditing, user, isSuperUser]);

  const loadTalentData = async () => {
    setLoading(true);
    setError(null);
    setTalent(null); // Clear previous data

    try {
      const { data, error: serviceError } = await talentService?.getTalentById(id);
      
      if (serviceError) {
        console.error('Service error loading talent:', serviceError);
        setError(serviceError?.message || 'Failed to load talent data');
      } else if (data) {
        console.log('Loaded talent data:', data); // Debug log
        setTalent(data);
      } else {
        console.warn('No talent data returned for ID:', id);
        setError('Talent profile not found');
      }
    } catch (err) {
      console.error('Error loading talent data:', err);
      setError('Failed to load talent data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTalentVideos = async () => {
    setVideosLoading(true);
    
    try {
      const { data, error: serviceError } = await talentService?.getTalentVideos(id);
      
      if (serviceError) {
        console.error('Service error loading talent videos:', serviceError);
        // Don't set main error, just log it
      } else {
        setTalentVideos(data || []);
      }
    } catch (err) {
      console.error('Error loading talent videos:', err);
      // Don't set main error, just log it
    } finally {
      setVideosLoading(false);
    }
  };

  const loadTalentRoles = async () => {
    setRolesLoading(true);
    
    try {
      const { data, error: serviceError } = await RoleService?.getRolesByTalent(id);
      
      if (serviceError) {
        console.error('Service error loading talent roles:', serviceError);
        // Don't set main error, just log it
      } else {
        setTalentRoles(data || []);
      }
    } catch (err) {
      console.error('Error loading talent roles:', err);
      // Don't set main error, just log it
    } finally {
      setRolesLoading(false);
    }
  };

  const loadTalentWarmUps = async () => {
    setWarmUpsLoading(true);
    try {
      const allWarmUps = await fetchWarmUps({ adminMode: true });
      const filtered = (allWarmUps || [])?.filter(wu => wu?.talent_id === id);
      setTalentWarmUps(filtered);
    } catch (err) {
      console.error('Error loading talent warm ups:', err);
    } finally {
      setWarmUpsLoading(false);
    }
  };

  const handleRoleClick = (roleId) => {
    navigate(`/role-detail-page/${roleId}`);
  };

  const handleRoleSearch = async (query) => {
    setRoleSearchQuery(query);
    if (!query?.trim()) {
      setRoleSearchResults([]);
      setShowRoleDropdown(false);
      return;
    }
    setRoleSearchLoading(true);
    setShowRoleDropdown(true);
    try {
      const { data, error: searchError } = await RoleService?.searchRolesFuzzy(query);
      if (!searchError) {
        // Filter out roles already assigned to this talent
        const assignedIds = new Set(talentRoles?.map(r => r?.id));
        setRoleSearchResults((data || [])?.filter(r => !assignedIds?.has(r?.id)));
      }
    } catch (err) {
      console.error('Role search error:', err);
    } finally {
      setRoleSearchLoading(false);
    }
  };

  const handleAssignRole = async (role) => {
    setAssigningRole(true);
    setShowRoleDropdown(false);
    setRoleSearchQuery('');
    setRoleSearchResults([]);
    try {
      await assignTalentToRole(role?.id, id);
      // Reload roles to reflect the change
      await loadTalentRoles();
    } catch (err) {
      console.error('Error assigning role:', err);
    } finally {
      setAssigningRole(false);
    }
  };

  const handleUnassignRole = async (roleId) => {
    setUnassigningRoleId(roleId);
    try {
      await assignTalentToRole(roleId, null);
      setTalentRoles(prev => prev?.filter(r => r?.id !== roleId));
    } catch (err) {
      console.error('Error unassigning role:', err);
    } finally {
      setUnassigningRoleId(null);
    }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    setError(null);
    
    try {
      let headshotUrl = formData?.headshot_url;

      // Handle headshot upload if there's a new file
      if (formData?.headshotFile) {
        const fileExt = formData?.headshotFile?.name?.split('.')?.pop();
        const fileName = `${id || 'new'}-${Date.now()}.${fileExt}`;
        const filePath = `talent-headshots/${fileName}`;

        // Delete old headshot if exists and we're updating
        if (id && talent?.headshot_url) {
          try {
            await supabase?.storage?.from('profile-images')?.remove([talent?.headshot_url]);
          } catch (deleteError) {
            console.error('Error deleting old headshot:', deleteError);
          }
        }

        // Upload new headshot
        const { error: uploadError } = await supabase?.storage?.from('profile-images')?.upload(filePath, formData?.headshotFile);

        if (uploadError) {
          throw new Error(`Failed to upload headshot: ${uploadError.message}`);
        }

        headshotUrl = filePath;
      }

      // Prepare talent data (exclude headshotFile from the data sent to API)
      const { headshotFile, ...talentDataToSave } = formData;
      const talentDataWithHeadshot = {
        ...talentDataToSave,
        headshot_url: headshotUrl
      };

      let result;
      if (id) {
        // Update existing talent
        result = await talentService?.updateTalent(id, talentDataWithHeadshot);
      } else {
        // Create new talent
        result = await talentService?.createTalent(talentDataWithHeadshot);
      }

      if (result?.error) {
        setError(result?.error);
        return false;
      }

      // Success - navigate back
      navigate('/talent-management-dashboard');
      return true;

    } catch (error) {
      console.error('Error saving talent:', error);
      setError(error?.message || 'Failed to save talent profile. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/talent-management-dashboard');
  };

  const handleRemoveVideo = async (videoId) => {
    setRemovingVideoId(videoId);
    try {
      const { error: updateError } = await supabase
        ?.from('song_videos')
        ?.update({ talent_id: null })
        ?.eq('id', videoId);
      if (!updateError) {
        setTalentVideos(prev => prev?.filter(v => v?.id !== videoId));
      } else {
        console.error('Error removing video from talent:', updateError);
      }
    } catch (err) {
      console.error('Error removing video from talent:', err);
    } finally {
      setRemovingVideoId(null);
    }
  };

  const handleRemoveWarmUp = async (warmUpId) => {
    setRemovingWarmUpId(warmUpId);
    try {
      await updateWarmUp(warmUpId, { talent_id: null });
      setTalentWarmUps(prev => prev?.filter(wu => wu?.id !== warmUpId));
    } catch (err) {
      console.error('Error removing warm up from talent:', err);
    } finally {
      setRemovingWarmUpId(null);
    }
  };

  if (!user || !isSuperUser()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
            <p className="text-blue-100">You need SuperUser privileges to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header />
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-xl text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
              aria-label="Back to talent management"
            >
              <Icon name="ArrowLeft" size={24} />
            </button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-white">
                {isEditing ? 'Edit Talent Profile' : 'Add New Talent Profile'}
              </h1>
              <p className="text-blue-100 font-body mt-1">
                {isEditing 
                  ? 'Update talent information and contact details'
                  : 'Create a new talent profile with contact and social media information'
                }
              </p>
            </div>
          </div>
          
          {/* Required fields notice */}
          <div className="bg-yellow-500 bg-opacity-20 border border-yellow-400 border-opacity-30 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={20} className="text-yellow-300 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-yellow-200 font-medium mb-1">Required Fields</h3>
                <p className="text-yellow-100 text-sm">
                  Only <strong>First Name</strong> and <strong>Last Name</strong> are required to submit this form. 
                  All other fields are optional and can be filled in later.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={20} className="text-red-800" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="space-y-6">
              {Array?.from({ length: 4 })?.map((_, i) => (
                <div key={i} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
                  <div className="h-6 bg-white bg-opacity-20 rounded-xl shimmer mb-4 w-1/3"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-full"></div>
                    <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        {!loading && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <TalentForm
              initialData={talent}
              onSave={handleSave}
              onCancel={handleCancel}
              saving={saving}
              isEditing={isEditing}
            />
          </div>
        )}

        {/* Associated Videos Section - Only show when editing */}
        {isEditing && !loading && (
          <div className="mt-8 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Video" size={24} className="text-blue-300" />
              <h2 className="text-2xl font-bold text-white">Lesson Videos</h2>
            </div>

            {videosLoading ? (
              <div className="space-y-4">
                {Array?.from({ length: 3 })?.map((_, i) => (
                  <div key={i} className="bg-white bg-opacity-5 rounded-xl p-4">
                    <div className="h-4 bg-white bg-opacity-20 rounded shimmer mb-2 w-2/3"></div>
                    <div className="h-3 bg-white bg-opacity-20 rounded shimmer w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : talentVideos?.length > 0 ? (
              <div className="space-y-4">
                {talentVideos?.map((video) => (
                  <div key={video?.id} className="bg-white bg-opacity-5 rounded-xl p-4 hover:bg-opacity-10 transition-all duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-1">
                          {video?.character_song_analyses?.songs?.title || video?.songs?.title || `Level ${video?.level || '?'}`}
                        </h3>
                        <div className="flex items-center space-x-4 text-blue-200 text-sm">
                          <span className="flex items-center space-x-1">
                            <Icon name="Play" size={14} />
                            <span>Level {video?.level || 'Unknown'}</span>
                          </span>
                          {video?.subsection && (
                            <span className="flex items-center space-x-1">
                              <Icon name="Hash" size={14} />
                              <span>{video?.subsection}</span>
                            </span>
                          )}
                          {video?.duration_seconds > 0 && (
                            <span className="flex items-center space-x-1">
                              <Icon name="Clock" size={14} />
                              <span>{Math.floor(video?.duration_seconds / 60)}:{(video?.duration_seconds % 60)?.toString()?.padStart(2, '0')}</span>
                            </span>
                          )}
                        </div>
                        <div className="text-blue-300 text-xs mt-2">
                          Added: {new Date(video?.created_at)?.toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveVideo(video?.id)}
                        disabled={removingVideoId === video?.id}
                        title="Remove video from talent"
                        className="ml-3 p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500 hover:bg-opacity-20 transition-colors duration-150 disabled:opacity-50 flex-shrink-0"
                      >
                        {removingVideoId === video?.id ? (
                          <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Icon name="X" size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Video" size={48} className="text-blue-300 opacity-50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Lesson Videos Assigned</h3>
                <p className="text-blue-200 text-sm">
                  This talent has not been assigned any lesson videos yet.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Warm Ups Section - Only show when editing */}
        {isEditing && !loading && (
          <div className="mt-8 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Zap" size={24} className="text-green-300" />
              <h2 className="text-2xl font-bold text-white">Warm Ups</h2>
            </div>

            {warmUpsLoading ? (
              <div className="space-y-4">
                {Array?.from({ length: 3 })?.map((_, i) => (
                  <div key={i} className="bg-white bg-opacity-5 rounded-xl p-4">
                    <div className="h-4 bg-white bg-opacity-20 rounded shimmer mb-2 w-2/3"></div>
                    <div className="h-3 bg-white bg-opacity-20 rounded shimmer w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : talentWarmUps?.length > 0 ? (
              <div className="space-y-4">
                {talentWarmUps?.map((warmUp) => (
                  <div key={warmUp?.id} className="bg-white bg-opacity-5 rounded-xl p-4 hover:bg-opacity-10 transition-all duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-1">
                          {warmUp?.title || 'Untitled Warm Up'}
                        </h3>
                        <div className="flex items-center gap-3 flex-wrap mt-1">
                          {warmUp?.difficulty_level && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              warmUp?.difficulty_level === 'Beginner' ?'bg-green-500 bg-opacity-20 text-green-300'
                                : warmUp?.difficulty_level === 'Intermediate' ?'bg-yellow-500 bg-opacity-20 text-yellow-300' :'bg-red-500 bg-opacity-20 text-red-300'
                            }`}>
                              {warmUp?.difficulty_level}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            warmUp?.published
                              ? 'bg-blue-500 bg-opacity-20 text-blue-300' :'bg-gray-500 bg-opacity-20 text-gray-300'
                          }`}>
                            {warmUp?.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        {warmUp?.description && (
                          <p className="text-blue-200 text-sm mt-2 line-clamp-2">{warmUp?.description}</p>
                        )}
                        <div className="text-blue-300 text-xs mt-2">
                          Added: {new Date(warmUp?.created_at)?.toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveWarmUp(warmUp?.id)}
                        disabled={removingWarmUpId === warmUp?.id}
                        title="Remove warm up from talent"
                        className="ml-3 p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500 hover:bg-opacity-20 transition-colors duration-150 disabled:opacity-50 flex-shrink-0"
                      >
                        {removingWarmUpId === warmUp?.id ? (
                          <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Icon name="X" size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Zap" size={48} className="text-green-300 opacity-50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Warm Ups Assigned</h3>
                <p className="text-blue-200 text-sm">
                  This talent has not been assigned any warm ups yet.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Associated Roles Section - Only show when editing */}
        {isEditing && !loading && (
          <div className="mt-8 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Users" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-bold text-white">Associated Roles</h2>
            </div>

            {/* Role Search / Assign */}
            <div className="mb-6 relative" ref={roleSearchRef}>
              <label className="block text-sm font-medium text-blue-200 mb-2">
                Assign a Role to this Talent
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Search" size={16} className="text-blue-300" />
                </div>
                <input
                  type="text"
                  value={roleSearchQuery}
                  onChange={(e) => handleRoleSearch(e?.target?.value)}
                  onFocus={() => roleSearchQuery?.trim() && setShowRoleDropdown(true)}
                  placeholder="Search roles by character name or musical..."
                  className="w-full pl-9 pr-4 py-2.5 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm"
                />
                {assigningRole && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <div className="w-4 h-4 border-2 border-purple-300 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Dropdown results */}
              {showRoleDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-indigo-900 border border-white border-opacity-20 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                  {roleSearchLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-5 h-5 border-2 border-purple-300 border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span className="text-blue-200 text-sm">Searching...</span>
                    </div>
                  ) : roleSearchResults?.length > 0 ? (
                    roleSearchResults?.map((role) => (
                      <button
                        key={role?.id}
                        onClick={() => handleAssignRole(role)}
                        className="w-full text-left px-4 py-3 hover:bg-white hover:bg-opacity-10 transition-colors duration-150 border-b border-white border-opacity-10 last:border-b-0"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-white font-medium text-sm">{role?.character_name}</span>
                            {role?.musicals?.title && (
                              <span className="text-purple-300 text-xs ml-2">— {role?.musicals?.title}</span>
                            )}
                            <div className="flex gap-2 mt-1">
                              {role?.voice_type && (
                                <span className="text-blue-300 text-xs capitalize">{role?.voice_type?.replace('_', ' ')}</span>
                              )}
                              {role?.role_type && (
                                <span className="text-purple-300 text-xs capitalize">{role?.role_type?.replace('_', ' ')}</span>
                              )}
                            </div>
                          </div>
                          <Icon name="Plus" size={16} className="text-purple-300 flex-shrink-0" />
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-blue-300 text-sm">
                      No unassigned roles found for "{roleSearchQuery}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Click outside to close dropdown */}
            {showRoleDropdown && (
              <div className="fixed inset-0 z-40" onClick={() => setShowRoleDropdown(false)} />
            )}

            {rolesLoading ? (
              <div className="space-y-4">
                {Array?.from({ length: 3 })?.map((_, i) => (
                  <div key={i} className="bg-white bg-opacity-5 rounded-xl p-4">
                    <div className="h-4 bg-white bg-opacity-20 rounded shimmer mb-2 w-2/3"></div>
                    <div className="h-3 bg-white bg-opacity-20 rounded shimmer w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : talentRoles?.length > 0 ? (
              <div className="space-y-4">
                {talentRoles?.map((role) => (
                  <div 
                    key={role?.id} 
                    className="bg-white bg-opacity-5 rounded-xl p-4 hover:bg-opacity-10 transition-all duration-200 group"
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => handleRoleClick(role?.id)}
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-semibold group-hover:text-purple-300 transition-colors">
                            {role?.character_name}
                          </h3>
                          {role?.is_principal && (
                            <span className="px-2 py-1 text-xs font-medium bg-yellow-500 bg-opacity-20 text-yellow-300 rounded-lg">
                              Principal
                            </span>
                          )}
                          {role?.role_type && (
                            <span className="px-2 py-1 text-xs font-medium bg-purple-500 bg-opacity-20 text-purple-300 rounded-lg capitalize">
                              {role?.role_type?.replace('_', ' ')}
                            </span>
                          )}
                        </div>

                        {role?.musicals?.title && (
                          <div className="text-purple-200 text-sm mb-2 flex items-center space-x-2">
                            <Icon name="Music" size={14} />
                            <span>{role?.musicals?.title}</span>
                            {role?.musicals?.year_premiered && (
                              <span className="text-purple-300">({role?.musicals?.year_premiered})</span>
                            )}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 text-blue-200 text-xs">
                          {role?.voice_type && (
                            <span className="flex items-center space-x-1 bg-blue-500 bg-opacity-10 px-2 py-1 rounded">
                              <Icon name="Mic2" size={12} />
                              <span className="capitalize">{role?.voice_type?.replace('_', ' ')}</span>
                            </span>
                          )}
                          {role?.vocal_range && (
                            <span className="flex items-center space-x-1 bg-blue-500 bg-opacity-10 px-2 py-1 rounded">
                              <Icon name="TrendingUp" size={12} />
                              <span>{role?.vocal_range}</span>
                            </span>
                          )}
                          {role?.age_range && (
                            <span className="flex items-center space-x-1 bg-blue-500 bg-opacity-10 px-2 py-1 rounded">
                              <Icon name="Calendar" size={12} />
                              <span>{role?.age_range}</span>
                            </span>
                          )}
                        </div>

                        {role?.description && (
                          <p className="text-blue-100 text-sm mt-2 line-clamp-2">
                            {role?.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        <button
                          onClick={() => handleUnassignRole(role?.id)}
                          disabled={unassigningRoleId === role?.id}
                          title="Remove role assignment"
                          className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500 hover:bg-opacity-20 transition-colors duration-150 disabled:opacity-50"
                        >
                          {unassigningRoleId === role?.id ? (
                            <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Icon name="X" size={16} />
                          )}
                        </button>
                        <Icon name="ChevronRight" size={16} className="text-purple-300 mt-0 group-hover:text-white transition-colors cursor-pointer" onClick={() => handleRoleClick(role?.id)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Users" size={48} className="text-purple-300 opacity-50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Roles Assigned</h3>
                <p className="text-blue-200 text-sm">
                  Use the search above to assign roles to this talent.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AddEditTalentProfile;